import { useCallback, useEffect, useRef, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";
import {
  isNovaPoshtaAddressPreparedEnabled,
  validateNovaPoshtaSelection,
  type WarehouseOption,
} from "./nova-poshta-reference-client.js";
import {
  NovaPoshtaSettlementCombobox,
  type NovaPoshtaSettlementValue,
} from "./nova-poshta-settlement-combobox-v2.js";
import { NovaPoshtaWarehouseCombobox } from "./nova-poshta-warehouse-combobox-v2.js";
import {
  emitDeliveryAnalytics,
  npDirectoryErrorCopy,
  npRateLimitBody,
  npRateLimitChangeCityLabel,
  npRateLimitRetryLabel,
  npRateLimitTitle,
} from "./nova-poshta-combobox-replace.js";
import { emitEvent } from "./events-v2.js";

export type NovaPoshtaDeliveryMode = "branch" | "parcel_locker" | "address";

export interface NovaPoshtaDeliverySectionProps {
  lang: StoreV2Lang;
  initialSelection?: NovaPoshtaReferenceSelection | null;
  onSelectionChange: (sel: NovaPoshtaReferenceSelection | null) => void;
  onManualFallbackChange: (manual: boolean) => void;
  onDisplayChange: (city: string, warehouse: string) => void;
  manualFallback: boolean;
  /** COMMERCE-176A — lock mode when parent owns delivery-method cards */
  forcedMode?: NovaPoshtaDeliveryMode;
  hideModeTabs?: boolean;
}

/** Debounce constants — used by combobox modules (R3/R4 regression surface). */
const SETTLEMENT_DEBOUNCE_MS = 350;
const WAREHOUSE_DEBOUNCE_MS = 200;
void SETTLEMENT_DEBOUNCE_MS;
void WAREHOUSE_DEBOUNCE_MS;

type ProviderErrorState = {
  message: string;
  rateLimited: boolean;
  retryAfterSec: number | null;
};

export function NovaPoshtaDeliverySection({
  lang,
  initialSelection,
  onSelectionChange,
  onManualFallbackChange,
  onDisplayChange,
  manualFallback,
  forcedMode,
  hideModeTabs = false,
}: NovaPoshtaDeliverySectionProps) {
  const locale = lang === "en" ? "en" : "uk";
  const copyLang = lang === "en" ? "en" : "uk";
  const addressPrepared = isNovaPoshtaAddressPreparedEnabled();
  const [mode, setMode] = useState<NovaPoshtaDeliveryMode>(
    forcedMode ??
      (initialSelection?.mode === "parcel_locker"
        ? "parcel_locker"
        : initialSelection?.mode === "address"
          ? "address"
          : "branch"),
  );
  const [selectedSettlement, setSelectedSettlement] = useState<NovaPoshtaSettlementValue | null>(
    initialSelection
      ? {
          ref: initialSelection.settlementRef,
          cityRef: initialSelection.cityRef ?? initialSelection.settlementRef,
          label: initialSelection.settlementLabel,
          region: initialSelection.region ?? null,
          area: initialSelection.area ?? null,
          settlementType: initialSelection.settlementType ?? null,
        }
      : null,
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseOption | null>(null);
  const [addressBuilding, setAddressBuilding] = useState(initialSelection?.building ?? "");
  const [addressFlat, setAddressFlat] = useState(initialSelection?.flat ?? "");
  const [addressStreet, setAddressStreet] = useState(initialSelection?.addressLine ?? "");
  const [providerError, setProviderError] = useState<ProviderErrorState | null>(null);
  /** CUSTOMER-R4.15-D — local saved-state outdated (NOT Nova Poshta API outage). */
  const [staleSelectionHint, setStaleSelectionHint] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [revalidating, setRevalidating] = useState(false);
  const [warehouseRetryKey, setWarehouseRetryKey] = useState(0);
  const restoredRef = useRef(false);
  const warehouseRetryRef = useRef<(() => void) | null>(null);

  const clearWarehouseState = useCallback(() => {
    setSelectedWarehouse(null);
    setProviderError(null);
    setStaleSelectionHint(null);
    setCountdown(null);
  }, []);

  useEffect(() => {
    if (!forcedMode || forcedMode === mode) return;
    setMode(forcedMode);
    clearWarehouseState();
  }, [forcedMode, mode, clearWarehouseState]);

  const handleProviderError = useCallback(
    (message: string | null, meta?: { rateLimited?: boolean; retryAfterSec?: number | null }) => {
      if (!message) {
        setProviderError(null);
        setCountdown(null);
        return;
      }
      const rateLimited = Boolean(meta?.rateLimited);
      const retryAfterSec = meta?.retryAfterSec ?? (rateLimited ? 5 : null);
      setProviderError({
        message,
        rateLimited,
        retryAfterSec,
      });
      if (rateLimited && retryAfterSec != null && retryAfterSec > 0) {
        setCountdown(retryAfterSec);
      } else {
        setCountdown(null);
      }
    },
    [],
  );

  useEffect(() => {
    if (countdown == null || countdown <= 0) return;
    const t = window.setTimeout(() => setCountdown((c) => (c == null ? null : Math.max(0, c - 1))), 1000);
    return () => window.clearTimeout(t);
  }, [countdown]);

  const emitSelection = useCallback(
    (
      settlement: NovaPoshtaSettlementValue | null,
      wh: WarehouseOption | null,
      deliveryMode: NovaPoshtaDeliveryMode,
    ) => {
      if (!settlement) {
        onSelectionChange(null);
        onDisplayChange("", "");
        return;
      }
      if (deliveryMode === "address") {
        const line = [addressStreet.trim(), addressBuilding.trim(), addressFlat.trim()]
          .filter(Boolean)
          .join(", ");
        const sel: NovaPoshtaReferenceSelection = {
          mode: "address",
          settlementRef: settlement.ref,
          cityRef: settlement.cityRef,
          settlementLabel: settlement.label,
          region: settlement.region,
          area: settlement.area,
          settlementType: settlement.settlementType,
          warehouseLabel: lang === "en" ? "Address delivery (prepared)" : "Адресна доставка (підготовлено)",
          locale,
          version: "117A-v1",
          ...(line ? { addressLine: line } : {}),
          ...(addressBuilding.trim() ? { building: addressBuilding.trim() } : {}),
          ...(addressFlat.trim() ? { flat: addressFlat.trim() } : {}),
        };
        onSelectionChange(sel);
        onDisplayChange(settlement.label, sel.warehouseLabel ?? line);
        return;
      }
      if (!wh) {
        const cityOnly: NovaPoshtaReferenceSelection = {
          mode: deliveryMode === "parcel_locker" ? "parcel_locker" : "warehouse",
          settlementRef: settlement.ref,
          cityRef: settlement.cityRef,
          settlementLabel: settlement.label,
          region: settlement.region,
          area: settlement.area,
          settlementType: settlement.settlementType,
          locale,
          version: "117A-v1",
        };
        onSelectionChange(cityOnly);
        onDisplayChange(settlement.label, "");
        return;
      }
      if (!wh.warehouseRef || wh.warehouseRef.length < 8) {
        const cityOnly: NovaPoshtaReferenceSelection = {
          mode: deliveryMode === "parcel_locker" ? "parcel_locker" : "warehouse",
          settlementRef: settlement.ref,
          cityRef: settlement.cityRef,
          settlementLabel: settlement.label,
          region: settlement.region,
          area: settlement.area,
          settlementType: settlement.settlementType,
          locale,
          version: "117A-v1",
        };
        onSelectionChange(cityOnly);
        onDisplayChange(settlement.label, "");
        return;
      }
      const expectedKind = deliveryMode === "parcel_locker" ? "parcel_locker" : "branch";
      if (deliveryMode === "parcel_locker" && wh.kind !== "parcel_locker") {
        const cityOnly: NovaPoshtaReferenceSelection = {
          mode: "parcel_locker",
          settlementRef: settlement.ref,
          cityRef: settlement.cityRef,
          settlementLabel: settlement.label,
          region: settlement.region,
          area: settlement.area,
          settlementType: settlement.settlementType,
          locale,
          version: "117A-v1",
        };
        onSelectionChange(cityOnly);
        onDisplayChange(settlement.label, "");
        return;
      }
      if (deliveryMode === "branch" && wh.kind === "parcel_locker") {
        const cityOnly: NovaPoshtaReferenceSelection = {
          mode: "warehouse",
          settlementRef: settlement.ref,
          cityRef: settlement.cityRef,
          settlementLabel: settlement.label,
          region: settlement.region,
          area: settlement.area,
          settlementType: settlement.settlementType,
          locale,
          version: "117A-v1",
        };
        onSelectionChange(cityOnly);
        onDisplayChange(settlement.label, "");
        return;
      }
      void expectedKind;
      const sel: NovaPoshtaReferenceSelection = {
        mode: deliveryMode === "parcel_locker" ? "parcel_locker" : "warehouse",
        settlementRef: settlement.ref,
        cityRef: settlement.cityRef,
        settlementLabel: settlement.label,
        region: settlement.region,
        area: settlement.area,
        settlementType: settlement.settlementType,
        warehouseRef: wh.warehouseRef,
        warehouseLabel: wh.label,
        warehouseNumber: wh.number,
        warehouseAddress: wh.shortAddress || wh.label,
        locale,
        version: "117A-v1",
      };
      onSelectionChange(sel);
      onDisplayChange(settlement.label, wh.label);
    },
    [addressBuilding, addressFlat, addressStreet, lang, locale, onDisplayChange, onSelectionChange],
  );

  useEffect(() => {
    if (restoredRef.current || !initialSelection?.settlementRef) return;
    restoredRef.current = true;
    setSelectedSettlement({
      ref: initialSelection.settlementRef,
      cityRef: initialSelection.cityRef ?? initialSelection.settlementRef,
      label: initialSelection.settlementLabel,
      region: initialSelection.region ?? null,
      area: initialSelection.area ?? null,
      settlementType: initialSelection.settlementType ?? null,
    });
    if (initialSelection.warehouseRef && initialSelection.warehouseLabel) {
      setSelectedWarehouse({
        warehouseRef: initialSelection.warehouseRef,
        label: initialSelection.warehouseLabel,
        shortAddress: initialSelection.warehouseLabel,
        number: null,
        kind: initialSelection.mode === "parcel_locker" ? "parcel_locker" : "branch",
        maxWeightKg: null,
      });
    }
    setRevalidating(true);
    void validateNovaPoshtaSelection(initialSelection).then((res) => {
      setRevalidating(false);
      if (!res.ok) {
        if (res.rateLimited) {
          // Temporary 429 must not erase a valid selected Ref.
          handleProviderError(res.errors[0]?.message ?? npDirectoryErrorCopy(copyLang), {
            rateLimited: true,
            retryAfterSec: res.retryAfterSec ?? 5,
          });
          return;
        }
        // CUSTOMER-R4.15-D — stale/invalid saved selection ≠ provider outage.
        // Keep city if present; clear only warehouse; ask to re-pick branch (neutral, not red NP-down).
        setProviderError(null);
        setStaleSelectionHint(
          lang === "en"
            ? initialSelection.mode === "parcel_locker"
              ? "Choose a current parcel locker"
              : initialSelection.mode === "address"
                ? "Confirm the courier address"
                : "Choose a current Nova Poshta branch"
            : initialSelection.mode === "parcel_locker"
              ? "Оберіть актуальний поштомат"
              : initialSelection.mode === "address"
                ? "Підтвердіть адресу для курʼєра"
                : "Оберіть актуальне відділення",
        );
        setSelectedWarehouse(null);
        if (initialSelection.settlementRef) {
          const kept: NovaPoshtaReferenceSelection = {
            mode: initialSelection.mode,
            settlementRef: initialSelection.settlementRef,
            settlementLabel: initialSelection.settlementLabel,
            locale: initialSelection.locale,
            version: initialSelection.version,
          };
          if (initialSelection.cityRef) kept.cityRef = initialSelection.cityRef;
          if (initialSelection.settlementType != null) kept.settlementType = initialSelection.settlementType;
          if (initialSelection.region != null) kept.region = initialSelection.region;
          if (initialSelection.area != null) kept.area = initialSelection.area;
          onSelectionChange(kept);
        } else {
          onSelectionChange(null);
        }
      }
    });
  }, [initialSelection, lang, copyLang, handleProviderError, onSelectionChange]);

  useEffect(() => {
    if (manualFallback) {
      onSelectionChange(null);
      return;
    }
  }, [manualFallback, onSelectionChange]);

  useEffect(() => {
    emitSelection(selectedSettlement, selectedWarehouse, mode);
  }, [selectedSettlement, selectedWarehouse, mode, addressBuilding, addressFlat, addressStreet, emitSelection]);

  const onSettlementChange = (next: NovaPoshtaSettlementValue | null) => {
    setSelectedSettlement(next);
    clearWarehouseState();
    setStaleSelectionHint(null);
    setProviderError(null);
    if (!next) {
      onSelectionChange(null);
      onDisplayChange("", "");
    } else {
      onDisplayChange(next.label, "");
    }
  };

  const switchMode = (id: NovaPoshtaDeliveryMode) => {
    if (id === mode) return;
    setMode(id);
    clearWarehouseState();
    setStaleSelectionHint(null);
    setProviderError(null);
    if (selectedSettlement) {
      onDisplayChange(selectedSettlement.label, "");
    }
    emitDeliveryAnalytics("delivery.type_changed", { deliveryType: id });
  };

  const retryDirectory = () => {
    setProviderError(null);
    setCountdown(null);
    setWarehouseRetryKey((k) => k + 1);
    warehouseRetryRef.current?.();
  };

  const changeCity = () => {
    setProviderError(null);
    setCountdown(null);
    onSettlementChange(null);
  };

  const modeTabs = (
    [
      ["branch", lang === "en" ? "Branch" : "Відділення"],
      ["parcel_locker", lang === "en" ? "Parcel locker" : "Поштомат"],
      ...(addressPrepared || forcedMode === "address" || hideModeTabs
        ? ([["address", lang === "en" ? "Courier" : "Кур'єр"]] as const)
        : []),
    ] as const
  );
  const courierFieldsEnabled = mode === "address" && (addressPrepared || Boolean(forcedMode) || hideModeTabs);

  return (
    <div className="sv2-np-ref" data-testid="sv2-np-reference-delivery">
      {!manualFallback && !hideModeTabs ? (
        <div
          className="sv2-delivery-method-cards sv2-np-mode-cards"
          role="radiogroup"
          aria-label={lang === "en" ? "Delivery type" : "Тип доставки"}
          data-testid="sv2-np-delivery-mode-tabs"
        >
          {modeTabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={mode === id}
              className={`sv2-delivery-method-card${mode === id ? " is-selected" : ""}`}
              data-testid={`sv2-np-mode-${id}`}
              onClick={() => switchMode(id)}
            >
              <span className="sv2-delivery-method-icon" aria-hidden>
                {id === "branch" ? "🏢" : id === "parcel_locker" ? "📦" : "🚚"}
              </span>
              <span className="sv2-delivery-method-text">
                <strong>{label}</strong>
                <span>
                  {id === "branch"
                    ? lang === "en"
                      ? "Pick up at a branch"
                      : "Отримати у відділенні Нової пошти"
                    : id === "parcel_locker"
                      ? lang === "en"
                        ? "Pick up at a locker"
                        : "Отримати у поштоматі"
                      : lang === "en"
                        ? "Courier to your address"
                        : "Доставка за адресою"}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <NovaPoshtaSettlementCombobox
        lang={lang}
        disabled={manualFallback}
        value={selectedSettlement}
        onChange={onSettlementChange}
        onProviderError={handleProviderError}
      />
      {selectedSettlement && !manualFallback ? (
        <div className="sv2-np-selected" data-testid="sv2-np-selected-settlement">
          {lang === "en" ? "Selected city:" : "Обрано місто:"}{" "}
          <strong>{selectedSettlement.label}</strong>
        </div>
      ) : null}

      {!manualFallback && courierFieldsEnabled ? (
        <div className="sv2-np-address-prepared" data-testid="sv2-np-address-prepared">
          <p className="sv2-muted sv2-np-hint">
            {lang === "en"
              ? "Enter courier address. Street directory may be completed manually when search is unavailable."
              : "Вкажіть адресу для курʼєра. За потреби вулицю можна ввести вручну."}
          </p>
          <div className="sv2-field">
            <label className="sv2-address-label">{lang === "en" ? "Street *" : "Вулиця *"}</label>
            <input
              className="sv2-input sv2-input-visible"
              value={addressStreet}
              onChange={(e) => setAddressStreet(e.target.value.slice(0, 120))}
            />
          </div>
          <div className="sv2-field">
            <label className="sv2-address-label">{lang === "en" ? "Building *" : "Будинок *"}</label>
            <input
              className="sv2-input sv2-input-visible"
              value={addressBuilding}
              onChange={(e) => setAddressBuilding(e.target.value.slice(0, 20))}
            />
          </div>
          <div className="sv2-field">
            <label className="sv2-address-label">{lang === "en" ? "Apartment / office" : "Квартира / офіс"}</label>
            <input
              className="sv2-input sv2-input-visible"
              value={addressFlat}
              onChange={(e) => setAddressFlat(e.target.value.slice(0, 20))}
            />
          </div>
        </div>
      ) : null}

      {!manualFallback && mode !== "address" ? (
        <NovaPoshtaWarehouseCombobox
          key={`${selectedSettlement?.cityRef || selectedSettlement?.ref || "none"}:${mode}:${warehouseRetryKey}`}
          lang={lang}
          disabled={manualFallback || !selectedSettlement}
          // CUSTOMER-R4.15 — prefer CityRef for warehouse directory (settlement entity Ref can differ).
          settlementRef={selectedSettlement?.cityRef || selectedSettlement?.ref || null}
          mode={mode === "parcel_locker" ? "parcel_locker" : "branch"}
          value={selectedWarehouse}
          onChange={(wh) => {
            setSelectedWarehouse(wh);
            // CUSTOMER-R4.15-E — valid current selection clears stale saved-branch hint permanently for this checkout.
            if (wh) {
              setStaleSelectionHint(null);
              setProviderError(null);
              emitEvent("shipping_branch_selected", { has_warehouse_ref: true });
            }
          }}
          onProviderError={handleProviderError}
          onRetryRef={warehouseRetryRef}
        />
      ) : null}
      {revalidating ? (
        <div className="sv2-muted sv2-np-hint">{lang === "en" ? "Checking selection…" : "Перевірка вибору…"}</div>
      ) : null}

      {selectedWarehouse && !manualFallback ? (
        <div className="sv2-np-selected is-valid" data-testid="sv2-np-selected-summary" data-selected="true">
          <span className="sv2-np-selected-check" aria-hidden="true">
            ✓
          </span>{" "}
          {lang === "en" ? "Selected:" : "Обрано:"}{" "}
          <strong>
            {selectedWarehouse.number ? `№${selectedWarehouse.number}` : selectedWarehouse.label}
          </strong>
          {selectedWarehouse.shortAddress ? (
            <div className="sv2-muted" style={{ marginTop: 4, fontSize: 13 }}>
              {selectedWarehouse.shortAddress}
            </div>
          ) : null}
          {selectedWarehouse.maxWeightKg != null ? (
            <div className="sv2-muted" style={{ marginTop: 2, fontSize: 12 }}>
              {lang === "en" ? `Up to ${selectedWarehouse.maxWeightKg} kg` : `До ${selectedWarehouse.maxWeightKg} кг`}
            </div>
          ) : null}
        </div>
      ) : null}

      {staleSelectionHint && !selectedWarehouse ? (
        <div
          className="sv2-muted sv2-np-hint"
          style={{ marginTop: 8 }}
          data-testid="sv2-np-stale-selection"
          data-error-class="stale_saved_selection"
        >
          <p style={{ margin: 0 }}>{staleSelectionHint}</p>
        </div>
      ) : null}

      {providerError?.rateLimited ? (
        <div className="sv2-alert sv2-alert-err" style={{ marginTop: 8 }} data-testid="sv2-np-rate-limit">
          <p>
            <strong>{npRateLimitTitle(copyLang)}</strong>
          </p>
          <p>{npRateLimitBody(copyLang)}</p>
          {countdown != null && countdown > 0 ? (
            <p className="sv2-muted" data-testid="sv2-np-rate-limit-countdown">
              {lang === "en" ? `Retry in ${countdown}s` : `Повтор через ${countdown} с`}
            </p>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <button
              type="button"
              className="sv2-np-retry"
              data-testid="sv2-np-rate-limit-retry"
              disabled={countdown != null && countdown > 0}
              onClick={retryDirectory}
            >
              {npRateLimitRetryLabel(copyLang)}
            </button>
            <button
              type="button"
              className="sv2-np-retry"
              data-testid="sv2-np-rate-limit-change-city"
              onClick={changeCity}
            >
              {npRateLimitChangeCityLabel(copyLang)}
            </button>
          </div>
        </div>
      ) : providerError ? (
        <div className="sv2-alert sv2-alert-err" style={{ marginTop: 8 }} data-testid="sv2-np-provider-error">
          <strong data-testid="sv2-np-outage-title">
            {lang === "en" ? "Nova Poshta temporarily unavailable" : "Нова Пошта тимчасово не відповідає"}
          </strong>
          <p>
            {providerError.message.includes("http") || /[A-Z_]{6,}/.test(providerError.message)
              ? npDirectoryErrorCopy(copyLang)
              : providerError.message}
          </p>
          <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            <button type="button" className="sv2-np-retry" data-testid="sv2-np-directory-retry" onClick={retryDirectory}>
              {lang === "en" ? "Try again" : "Спробувати ще раз"}
            </button>
            <button
              type="button"
              className="sv2-np-retry"
              data-testid="sv2-np-manual-entry-cta"
              onClick={() => {
                onManualFallbackChange(true);
                onSelectionChange(null);
                clearWarehouseState();
                onDisplayChange("", "");
              }}
            >
              {lang === "en" ? "Couldn't find address?" : "Не знайшли адресу?"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="sv2-np-manual-fallback-wrap" data-testid="sv2-np-manual-fallback-wrap">
        {manualFallback ? (
          <div className="sv2-np-manual-active" data-testid="sv2-np-manual-mode-active">
            <strong>{lang === "en" ? "Manual address" : "Ручне введення адреси"}</strong>
            <p className="sv2-muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
              {lang === "en"
                ? "Nova Poshta branch is not selected. Manager will verify the address before TTN."
                : "Відділення Нової пошти не обрано. Менеджер перевірить адресу перед ТТН."}
            </p>
            <button
              type="button"
              className="sv2-np-retry"
              data-testid="sv2-np-manual-exit"
              onClick={() => onManualFallbackChange(false)}
            >
              {lang === "en" ? "Back to branch search" : "Повернутися до пошуку відділення"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="sv2-np-manual-link"
            data-testid="sv2-np-manual-entry-link"
            onClick={() => {
              onManualFallbackChange(true);
              onSelectionChange(null);
              clearWarehouseState();
              onDisplayChange("", "");
            }}
          >
            {lang === "en" ? "Couldn't find the address? Enter manually" : "Не знайшли адресу? Ввести вручну"}
          </button>
        )}
      </div>
    </div>
  );
}
